import { createDefaultInsetMap, type InsetDefaultContent } from './insetDefaults';
import { isLabelFontFamilyId, isLabelFontStyle, isLabelFontWeight } from '../labels/fonts';
import { supportsAuthoredItalic } from '../typography/fonts';
import type { MapView } from '../map/model';
import { isHexColour } from '../styles/colour';
import {
  COMPOSITION_LOGO_MAX_BYTES,
  COMPOSITION_LOGO_MAX_ASPECT_RATIO,
  COMPOSITION_LOGO_MIN_NORMALIZED_SIZE,
  isCompositionLogoDataValid,
  isUsableCompositionLogoFilename,
} from './logo';
import {
  COMPOSITION_SCALE_BAR_UNITS,
  COMPOSITION_NORTH_ARROW_STYLES,
  COMPOSITION_SCALE_BAR_THICKNESS_MAX_MM,
  COMPOSITION_SCALE_BAR_THICKNESS_MIN_MM,
  COMPOSITION_SOURCES_ATTRIBUTION_MAX_PT,
  COMPOSITION_SOURCES_ATTRIBUTION_MIN_PT,
  COMPOSITION_TYPOGRAPHY_MIN_PT,
  COMPOSITION_CUSTOM_PAGE_MAX_MM,
  COMPOSITION_CUSTOM_PAGE_MIN_MM,
  COMPOSITION_LEGEND_CORNER_RADIUS_MAX_MM,
  COMPOSITION_LEGEND_SPACING_MAX_MM,
  COMPOSITION_PAGE_ORIENTATIONS,
  COMPOSITION_PAGE_PRESETS,
  createLegacyComposition,
} from './model';
import type {
  CompositionCamera,
  CompositionDraft,
  CompositionLogo,
  CompositionTypography,
  NormalizedPoint,
  NormalizedRect,
} from './model';
import {
  COMPOSITION_SCALE_DENOMINATOR_MAX,
  COMPOSITION_SCALE_DENOMINATOR_MIN,
  LEGACY_COMPOSITION_PIXELS_PER_MM,
  scaleDenominatorForZoom,
} from './scale';

type UnknownRecord = Record<string, unknown>;

export const COMPOSITION_TEXT_MAX_LENGTH = 512;
export const COMPOSITION_LAYER_EXCLUSIONS_MAX = 1_000;

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function hasExactKeys(value: UnknownRecord, keys: readonly string[]): boolean {
  const actual = Object.keys(value);
  return actual.length === keys.length && actual.every((key) => keys.includes(key));
}

function hasKeysWithOptional(
  value: UnknownRecord,
  keys: readonly string[],
  optional: readonly string[],
): boolean {
  return (
    keys.every((key) => key in value) &&
    Object.keys(value).every((key) => keys.includes(key) || optional.includes(key))
  );
}

function isScaleBarPanel(value: unknown): boolean {
  if (
    !isRecord(value) ||
    !hasExactKeys(value, ['background', 'border', 'cornerRadiusMm', 'paddingMm'])
  )
    return false;
  const { background, border } = value;
  return (
    isRecord(background) &&
    hasExactKeys(background, ['enabled', 'colour', 'opacity']) &&
    typeof background.enabled === 'boolean' &&
    isHexColour(background.colour) &&
    finiteInRange(background.opacity, 0, 100) &&
    isRecord(border) &&
    hasExactKeys(border, ['enabled', 'widthMm', 'colour']) &&
    typeof border.enabled === 'boolean' &&
    isHexColour(border.colour) &&
    finiteInRange(border.widthMm, 0.1, 5) &&
    finiteInRange(value.cornerRadiusMm, 0, 50) &&
    finiteInRange(value.paddingMm, 0, 50)
  );
}

function finiteInRange(value: unknown, minimum: number, maximum: number): value is number {
  return (
    typeof value === 'number' && Number.isFinite(value) && value >= minimum && value <= maximum
  );
}

function isPoint(value: unknown): value is NormalizedPoint {
  return (
    isRecord(value) &&
    hasExactKeys(value, ['x', 'y']) &&
    finiteInRange(value.x, 0, 0.98) &&
    finiteInRange(value.y, 0, 0.98)
  );
}

function isRect(value: unknown, minimumHeight = 0.05): value is NormalizedRect {
  return (
    isRecord(value) &&
    hasExactKeys(value, ['x', 'y', 'width', 'height']) &&
    finiteInRange(value.x, 0, 1) &&
    finiteInRange(value.y, 0, 1) &&
    finiteInRange(value.width, 0.05, 1) &&
    finiteInRange(value.height, minimumHeight, 1) &&
    value.x + value.width <= 1 + Number.EPSILON &&
    value.y + value.height <= 1 + Number.EPSILON
  );
}

function isLogoRect(value: unknown): value is NormalizedRect {
  return (
    isRecord(value) &&
    hasExactKeys(value, ['x', 'y', 'width', 'height']) &&
    finiteInRange(value.x, 0, 1) &&
    finiteInRange(value.y, 0, 1) &&
    finiteInRange(value.width, COMPOSITION_LOGO_MIN_NORMALIZED_SIZE, 1) &&
    finiteInRange(value.height, COMPOSITION_LOGO_MIN_NORMALIZED_SIZE, 1) &&
    value.x + value.width <= 1 + Number.EPSILON &&
    value.y + value.height <= 1 + Number.EPSILON
  );
}

function isLogo(value: unknown): value is CompositionLogo {
  if (
    !isRecord(value) ||
    (!hasExactKeys(value, ['mimeType', 'dataUrl', 'aspectRatio', 'bounds']) &&
      !hasExactKeys(value, ['filename', 'mimeType', 'dataUrl', 'aspectRatio', 'bounds'])) ||
    ('filename' in value && !isUsableCompositionLogoFilename(value.filename)) ||
    (value.mimeType !== 'image/png' && value.mimeType !== 'image/jpeg') ||
    typeof value.dataUrl !== 'string' ||
    !finiteInRange(
      value.aspectRatio,
      1 / COMPOSITION_LOGO_MAX_ASPECT_RATIO,
      COMPOSITION_LOGO_MAX_ASPECT_RATIO,
    ) ||
    !isLogoRect(value.bounds)
  )
    return false;
  const prefix = `data:${value.mimeType};base64,`;
  return (
    value.dataUrl.startsWith(prefix) &&
    value.dataUrl.length > prefix.length &&
    value.dataUrl.length <= prefix.length + Math.ceil((COMPOSITION_LOGO_MAX_BYTES * 4) / 3) + 4
  );
}

function normaliseLogo(value: unknown): CompositionLogo | null {
  if (!isRecord(value)) return null;
  const candidate = { ...value };
  if ('filename' in candidate && !isUsableCompositionLogoFilename(candidate.filename)) {
    delete candidate.filename;
  }
  return isLogo(candidate) && isCompositionLogoDataValid(candidate) ? candidate : null;
}

function isInsetMap(value: unknown): boolean {
  if (
    !isRecord(value) ||
    !hasKeysWithOptional(
      value,
      [
        'bounds',
        'contextMargin',
        'border',
        'cornerRadiusMm',
        'indicator',
        'hiddenLayerIds',
        'hiddenAnnotationLayerIds',
        'hiddenAnnotationIds',
      ],
      ['enabled'],
    )
  )
    return false;
  const { border, indicator } = value;
  return (
    isRect(value.bounds) &&
    (!('enabled' in value) || typeof value.enabled === 'boolean') &&
    isLayerIdList(value.hiddenLayerIds) &&
    isLayerIdList(value.hiddenAnnotationLayerIds) &&
    isLayerIdList(value.hiddenAnnotationIds, 5_000) &&
    finiteInRange(value.contextMargin, 0, 500) &&
    finiteInRange(value.cornerRadiusMm, 0, 50) &&
    isRecord(border) &&
    hasExactKeys(border, ['enabled', 'widthMm', 'colour']) &&
    typeof border.enabled === 'boolean' &&
    finiteInRange(border.widthMm, 0.1, 5) &&
    isHexColour(border.colour) &&
    isRecord(indicator) &&
    hasExactKeys(indicator, ['outlineColour', 'outlineWidthMm', 'fillColour', 'fillOpacity']) &&
    isHexColour(indicator.outlineColour) &&
    isHexColour(indicator.fillColour) &&
    finiteInRange(indicator.outlineWidthMm, 0.1, 5) &&
    finiteInRange(indicator.fillOpacity, 0, 100)
  );
}

function isCenter(value: unknown): value is readonly [number, number] {
  return (
    Array.isArray(value) &&
    value.length === 2 &&
    finiteInRange(value[0], -180, 180) &&
    finiteInRange(value[1], -90, 90)
  );
}

function isCamera(value: unknown): value is CompositionCamera {
  return (
    isRecord(value) &&
    hasExactKeys(value, ['center', 'scaleDenominator', 'bearing', 'pitch']) &&
    isCenter(value.center) &&
    finiteInRange(
      value.scaleDenominator,
      COMPOSITION_SCALE_DENOMINATOR_MIN,
      COMPOSITION_SCALE_DENOMINATOR_MAX,
    ) &&
    finiteInRange(value.bearing, -180, 180) &&
    finiteInRange(value.pitch, 0, 85)
  );
}

function normaliseCamera(value: unknown): CompositionCamera | null {
  if (isCamera(value)) return value;
  if (
    !isRecord(value) ||
    !hasExactKeys(value, ['center', 'zoom', 'bearing', 'pitch']) ||
    !isCenter(value.center) ||
    !finiteInRange(value.zoom, 0, 24) ||
    !finiteInRange(value.bearing, -180, 180) ||
    !finiteInRange(value.pitch, 0, 85)
  ) {
    return null;
  }
  const scaleDenominator = scaleDenominatorForZoom(
    value.zoom,
    value.center[1],
    LEGACY_COMPOSITION_PIXELS_PER_MM,
  );
  if (
    scaleDenominator === null ||
    !finiteInRange(
      scaleDenominator,
      COMPOSITION_SCALE_DENOMINATOR_MIN,
      COMPOSITION_SCALE_DENOMINATOR_MAX,
    )
  ) {
    return null;
  }
  return {
    center: [...value.center],
    scaleDenominator,
    bearing: value.bearing,
    pitch: value.pitch,
  };
}

function isTypography(value: unknown): value is CompositionTypography {
  return (
    isRecord(value) &&
    hasExactKeys(value, [
      'fontFamily',
      'fontWeight',
      'fontStyle',
      'fontSizePt',
      'colour',
      'alignment',
    ]) &&
    isLabelFontFamilyId(value.fontFamily) &&
    isLabelFontWeight(value.fontWeight) &&
    isLabelFontStyle(value.fontStyle) &&
    (value.fontStyle !== 'italic' || supportsAuthoredItalic(value.fontFamily)) &&
    finiteInRange(value.fontSizePt, COMPOSITION_TYPOGRAPHY_MIN_PT, 96) &&
    isHexColour(value.colour) &&
    (value.alignment === 'left' || value.alignment === 'centre' || value.alignment === 'right')
  );
}

function isTextElement(value: unknown): boolean {
  return (
    isRecord(value) &&
    hasExactKeys(value, ['enabled', 'text', 'position', 'typography']) &&
    typeof value.enabled === 'boolean' &&
    typeof value.text === 'string' &&
    value.text.length <= COMPOSITION_TEXT_MAX_LENGTH &&
    isPoint(value.position) &&
    isTypography(value.typography)
  );
}

function isLayerIdList(
  value: unknown,
  maximum = COMPOSITION_LAYER_EXCLUSIONS_MAX,
): value is readonly string[] {
  return (
    Array.isArray(value) &&
    value.length <= maximum &&
    value.every((id) => typeof id === 'string' && id.length > 0 && id.length <= 128) &&
    new Set(value).size === value.length
  );
}

export function isCompositionDraftValid(value: unknown): value is CompositionDraft {
  if (
    !isRecord(value) ||
    !hasExactKeys(value, [
      'page',
      'mapFrame',
      'title',
      'subtitle',
      'legend',
      'scaleBar',
      'northArrow',
      'sourcesAttribution',
      'logo',
      'insetMap',
    ])
  )
    return false;
  const { page, mapFrame, legend, scaleBar, northArrow, sourcesAttribution } = value;
  if (
    !isRecord(page) ||
    !hasExactKeys(page, [
      'preset',
      'orientation',
      'backgroundColour',
      'scaleLayoutWithPage',
      'customWidthMm',
      'customHeightMm',
    ]) ||
    !COMPOSITION_PAGE_PRESETS.includes(page.preset as never) ||
    !COMPOSITION_PAGE_ORIENTATIONS.includes(page.orientation as never) ||
    !isHexColour(page.backgroundColour) ||
    typeof page.scaleLayoutWithPage !== 'boolean'
  )
    return false;
  const customDimensionsValid =
    page.preset === 'custom'
      ? finiteInRange(
          page.customWidthMm,
          COMPOSITION_CUSTOM_PAGE_MIN_MM,
          COMPOSITION_CUSTOM_PAGE_MAX_MM,
        ) &&
        finiteInRange(
          page.customHeightMm,
          COMPOSITION_CUSTOM_PAGE_MIN_MM,
          COMPOSITION_CUSTOM_PAGE_MAX_MM,
        )
      : page.customWidthMm === null && page.customHeightMm === null;
  return (
    customDimensionsValid &&
    isRecord(mapFrame) &&
    hasKeysWithOptional(mapFrame, ['bounds', 'camera', 'border'], ['cornerRadiusMm']) &&
    (!('cornerRadiusMm' in mapFrame) || finiteInRange(mapFrame.cornerRadiusMm, 0, 50)) &&
    isRect(mapFrame.bounds) &&
    isCamera(mapFrame.camera) &&
    isRecord(mapFrame.border) &&
    hasExactKeys(mapFrame.border, ['enabled', 'widthMm', 'colour']) &&
    typeof mapFrame.border.enabled === 'boolean' &&
    finiteInRange(mapFrame.border.widthMm, 0.1, 5) &&
    isHexColour(mapFrame.border.colour) &&
    isTextElement(value.title) &&
    isTextElement(value.subtitle) &&
    isRecord(legend) &&
    hasKeysWithOptional(
      legend,
      [
        'enabled',
        'autoFit',
        'bounds',
        'background',
        'border',
        'cornerRadiusMm',
        'paddingTopMm',
        'paddingRightMm',
        'paddingBottomMm',
        'paddingLeftMm',
        'groupSpacingMm',
        'itemSpacingMm',
        'bodyTypography',
        'heading',
        'excludedLayerIds',
        'hiddenGroupHeadingIds',
        'excludedEntryKeysByLayer',
      ],
      ['autoFitAnchor', 'columnCount', 'repeatContinuationHeadings', 'symbolSizePercent'],
    ) &&
    (!('symbolSizePercent' in legend) || finiteInRange(legend.symbolSizePercent, 75, 150)) &&
    (!('columnCount' in legend) || [1, 2, 3].includes(legend.columnCount as number)) &&
    (!('repeatContinuationHeadings' in legend) ||
      typeof legend.repeatContinuationHeadings === 'boolean') &&
    (!('autoFitAnchor' in legend) ||
      legend.autoFitAnchor === 'top-right' ||
      legend.autoFitAnchor === 'bottom-right') &&
    typeof legend.enabled === 'boolean' &&
    typeof legend.autoFit === 'boolean' &&
    isRect(legend.bounds, 0.001) &&
    isRecord(legend.background) &&
    hasExactKeys(legend.background, ['enabled', 'colour', 'opacity']) &&
    typeof legend.background.enabled === 'boolean' &&
    isHexColour(legend.background.colour) &&
    finiteInRange(legend.background.opacity, 0, 100) &&
    isRecord(legend.border) &&
    hasExactKeys(legend.border, ['enabled', 'widthMm', 'colour']) &&
    typeof legend.border.enabled === 'boolean' &&
    finiteInRange(legend.border.widthMm, 0.1, 5) &&
    isHexColour(legend.border.colour) &&
    finiteInRange(legend.cornerRadiusMm, 0, COMPOSITION_LEGEND_CORNER_RADIUS_MAX_MM) &&
    finiteInRange(legend.paddingTopMm, 0, COMPOSITION_LEGEND_SPACING_MAX_MM) &&
    finiteInRange(legend.paddingRightMm, 0, COMPOSITION_LEGEND_SPACING_MAX_MM) &&
    finiteInRange(legend.paddingBottomMm, 0, COMPOSITION_LEGEND_SPACING_MAX_MM) &&
    finiteInRange(legend.paddingLeftMm, 0, COMPOSITION_LEGEND_SPACING_MAX_MM) &&
    finiteInRange(legend.groupSpacingMm, 0, COMPOSITION_LEGEND_SPACING_MAX_MM) &&
    finiteInRange(legend.itemSpacingMm, 0, COMPOSITION_LEGEND_SPACING_MAX_MM) &&
    isTypography(legend.bodyTypography) &&
    isRecord(legend.heading) &&
    hasExactKeys(legend.heading, ['enabled', 'text', 'typography']) &&
    typeof legend.heading.enabled === 'boolean' &&
    typeof legend.heading.text === 'string' &&
    legend.heading.text.length <= COMPOSITION_TEXT_MAX_LENGTH &&
    isTypography(legend.heading.typography) &&
    isLayerIdList(legend.excludedLayerIds) &&
    isLayerIdList(legend.hiddenGroupHeadingIds) &&
    isRecord(legend.excludedEntryKeysByLayer) &&
    Object.entries(legend.excludedEntryKeysByLayer).every(
      ([layerId, keys]) =>
        layerId.length > 0 &&
        Array.isArray(keys) &&
        keys.every((key) => typeof key === 'string' && key.length > 0) &&
        new Set(keys).size === keys.length,
    ) &&
    isRecord(scaleBar) &&
    hasKeysWithOptional(
      scaleBar,
      ['enabled', 'position', 'units', 'maximumWidthMm', 'colour', 'fontSizePt', 'thicknessMm'],
      ['style', 'panel', 'labelPosition'],
    ) &&
    (!('style' in scaleBar) ||
      scaleBar.style === 'line' ||
      scaleBar.style === 'segmented' ||
      scaleBar.style === 'simple-segmented') &&
    (!('labelPosition' in scaleBar) ||
      scaleBar.labelPosition === 'above' ||
      scaleBar.labelPosition === 'below') &&
    (!('panel' in scaleBar) || isScaleBarPanel(scaleBar.panel)) &&
    typeof scaleBar.enabled === 'boolean' &&
    isPoint(scaleBar.position) &&
    COMPOSITION_SCALE_BAR_UNITS.includes(scaleBar.units as never) &&
    finiteInRange(scaleBar.maximumWidthMm, 10, 100) &&
    isHexColour(scaleBar.colour) &&
    finiteInRange(scaleBar.fontSizePt, COMPOSITION_TYPOGRAPHY_MIN_PT, 24) &&
    finiteInRange(
      scaleBar.thicknessMm,
      COMPOSITION_SCALE_BAR_THICKNESS_MIN_MM,
      COMPOSITION_SCALE_BAR_THICKNESS_MAX_MM,
    ) &&
    isRecord(northArrow) &&
    hasExactKeys(northArrow, ['enabled', 'position', 'sizeMm', 'colour', 'showLabel', 'style']) &&
    typeof northArrow.enabled === 'boolean' &&
    isPoint(northArrow.position) &&
    finiteInRange(northArrow.sizeMm, 5, 50) &&
    isHexColour(northArrow.colour) &&
    typeof northArrow.showLabel === 'boolean' &&
    COMPOSITION_NORTH_ARROW_STYLES.includes(northArrow.style as never) &&
    isRecord(sourcesAttribution) &&
    hasKeysWithOptional(sourcesAttribution, ['position', 'typography'], ['width']) &&
    (!('width' in sourcesAttribution) || finiteInRange(sourcesAttribution.width, 0.02, 1)) &&
    isRecord(sourcesAttribution.position) &&
    hasExactKeys(sourcesAttribution.position, ['x', 'y']) &&
    finiteInRange(sourcesAttribution.position.x, 0, 1) &&
    finiteInRange(sourcesAttribution.position.y, 0, 1) &&
    isTypography(sourcesAttribution.typography) &&
    finiteInRange(
      sourcesAttribution.typography.fontSizePt,
      COMPOSITION_SOURCES_ATTRIBUTION_MIN_PT,
      COMPOSITION_SOURCES_ATTRIBUTION_MAX_PT,
    ) &&
    (value.logo === null || isLogo(value.logo)) &&
    (value.insetMap === null || isInsetMap(value.insetMap))
  );
}

/** Upgrades the exact Milestone 5A composition shape to current v1 defaults. */
export function normaliseCompositionDraft(
  value: unknown,
  camera: MapView,
  projectTitle: string,
  insetContent: InsetDefaultContent = {},
): CompositionDraft | null {
  if (isCompositionDraftValid(value)) {
    return value.logo === null || isCompositionLogoDataValid(value.logo)
      ? value
      : { ...value, logo: null };
  }
  if (!isRecord(value)) return null;
  const legacyKeys = ['page', 'mapFrame', 'title', 'subtitle', 'legend'] as const;
  // Additive fields may coexist with any supported pre-release furniture shape.
  const keysValue = { ...value };
  delete keysValue.insetMap;
  const legacyWithSourcesKeys = [...legacyKeys, 'sourcesAttribution'] as const;
  const currentKeys = [...legacyKeys, 'scaleBar', 'northArrow'] as const;
  const latestKeys = [...currentKeys, 'sourcesAttribution'] as const;
  const logoKeys = [...latestKeys, 'logo'] as const;
  if (
    !hasExactKeys(keysValue, legacyKeys) &&
    !hasExactKeys(keysValue, legacyWithSourcesKeys) &&
    !hasExactKeys(keysValue, currentKeys) &&
    !hasExactKeys(keysValue, latestKeys) &&
    !hasExactKeys(keysValue, logoKeys)
  )
    return null;
  const defaults = createLegacyComposition(camera, projectTitle);
  const defaultInsetContent = createDefaultInsetMap(insetContent);
  const { page, mapFrame, title, subtitle, legend } = value;
  if (isRecord(mapFrame)) {
    const migratedCamera = normaliseCamera(mapFrame.camera);
    if (migratedCamera) {
      const migratedLegend = isRecord(legend)
        ? {
            ...legend,
            ...(!('autoFit' in legend) ? { autoFit: false } : {}),
            ...(!('excludedEntryKeysByLayer' in legend) ? { excludedEntryKeysByLayer: {} } : {}),
            ...(!('hiddenGroupHeadingIds' in legend) ? { hiddenGroupHeadingIds: [] } : {}),
            ...(!('cornerRadiusMm' in legend)
              ? { cornerRadiusMm: defaults.legend.cornerRadiusMm }
              : {}),
            ...(!('paddingTopMm' in legend)
              ? {
                  paddingTopMm: defaults.legend.paddingTopMm,
                  paddingRightMm: defaults.legend.paddingRightMm,
                  paddingBottomMm: defaults.legend.paddingBottomMm,
                  paddingLeftMm: defaults.legend.paddingLeftMm,
                  groupSpacingMm: defaults.legend.groupSpacingMm,
                  itemSpacingMm: defaults.legend.itemSpacingMm,
                }
              : {}),
          }
        : legend;
      const withCurrentDefaults = {
        ...value,
        page: isRecord(page)
          ? { ...page, scaleLayoutWithPage: page.scaleLayoutWithPage ?? true }
          : page,
        mapFrame: { ...mapFrame, camera: migratedCamera },
        legend: migratedLegend,
        scaleBar:
          'scaleBar' in value && isRecord(value.scaleBar)
            ? {
                ...value.scaleBar,
                ...(!('thicknessMm' in value.scaleBar)
                  ? { thicknessMm: defaults.scaleBar.thicknessMm }
                  : {}),
              }
            : defaults.scaleBar,
        northArrow:
          'northArrow' in value && isRecord(value.northArrow)
            ? {
                ...value.northArrow,
                ...(!('colour' in value.northArrow) ? { colour: defaults.northArrow.colour } : {}),
                ...(!('style' in value.northArrow) ? { style: defaults.northArrow.style } : {}),
              }
            : defaults.northArrow,
        sourcesAttribution:
          'sourcesAttribution' in value && isRecord(value.sourcesAttribution)
            ? value.sourcesAttribution
            : defaults.sourcesAttribution,
        logo: 'logo' in value ? normaliseLogo(value.logo) : null,
        insetMap: isRecord(value.insetMap)
          ? {
              hiddenLayerIds: defaultInsetContent.hiddenLayerIds,
              hiddenAnnotationLayerIds:
                'hiddenAnnotationIds' in value.insetMap
                  ? []
                  : defaultInsetContent.hiddenAnnotationLayerIds,
              hiddenAnnotationIds: [],
              ...value.insetMap,
              indicator: isRecord(value.insetMap.indicator)
                ? { ...defaultInsetContent.indicator, ...value.insetMap.indicator }
                : 'indicator' in value.insetMap
                  ? value.insetMap.indicator
                  : defaultInsetContent.indicator,
            }
          : (value.insetMap ?? null),
      };
      if (isCompositionDraftValid(withCurrentDefaults)) return withCurrentDefaults;
      if (isRecord(migratedLegend) && !('border' in migratedLegend)) {
        const withLegendBorder = {
          ...withCurrentDefaults,
          legend: { ...migratedLegend, border: defaults.legend.border },
        };
        if (isCompositionDraftValid(withLegendBorder)) return withLegendBorder;
      }
    }
  }
  if (
    !isRecord(page) ||
    !hasExactKeys(page, ['preset', 'orientation', 'backgroundColour']) ||
    page.preset === 'custom' ||
    !COMPOSITION_PAGE_PRESETS.includes(page.preset as never) ||
    !COMPOSITION_PAGE_ORIENTATIONS.includes(page.orientation as never) ||
    !isHexColour(page.backgroundColour) ||
    !isRecord(mapFrame) ||
    !hasExactKeys(mapFrame, ['bounds', 'camera', 'border']) ||
    !isRect(mapFrame.bounds) ||
    !normaliseCamera(mapFrame.camera) ||
    !isRecord(mapFrame.border) ||
    !hasExactKeys(mapFrame.border, ['enabled', 'widthMm', 'colour']) ||
    typeof mapFrame.border.enabled !== 'boolean' ||
    !finiteInRange(mapFrame.border.widthMm, 0.1, 5) ||
    !isHexColour(mapFrame.border.colour) ||
    !isRecord(title) ||
    !isRecord(subtitle) ||
    ![title, subtitle].every(
      (element) =>
        hasExactKeys(element, ['enabled', 'text', 'position', 'fontSizePt']) &&
        typeof element.enabled === 'boolean' &&
        typeof element.text === 'string' &&
        element.text.length <= COMPOSITION_TEXT_MAX_LENGTH &&
        isPoint(element.position) &&
        finiteInRange(element.fontSizePt, COMPOSITION_TYPOGRAPHY_MIN_PT, 96),
    ) ||
    !isRecord(legend) ||
    !hasExactKeys(legend, ['enabled', 'position', 'background']) ||
    typeof legend.enabled !== 'boolean' ||
    !isPoint(legend.position) ||
    typeof legend.background !== 'boolean'
  )
    return null;

  const migrated: CompositionDraft = {
    page: {
      preset: page.preset as CompositionDraft['page']['preset'],
      orientation: page.orientation as CompositionDraft['page']['orientation'],
      backgroundColour: page.backgroundColour,
      scaleLayoutWithPage: true,
      customWidthMm: null,
      customHeightMm: null,
    },
    mapFrame: {
      bounds: mapFrame.bounds as NormalizedRect,
      camera: normaliseCamera(mapFrame.camera) as CompositionCamera,
      border: mapFrame.border as CompositionDraft['mapFrame']['border'],
    },
    title: {
      enabled: title.enabled as boolean,
      text: title.text as string,
      position: title.position as NormalizedPoint,
      typography: { ...defaults.title.typography, fontSizePt: title.fontSizePt as number },
    },
    subtitle: {
      enabled: subtitle.enabled as boolean,
      text: subtitle.text as string,
      position: subtitle.position as NormalizedPoint,
      typography: { ...defaults.subtitle.typography, fontSizePt: subtitle.fontSizePt as number },
    },
    legend: {
      ...defaults.legend,
      enabled: legend.enabled as boolean,
      bounds: {
        ...defaults.legend.bounds,
        x: (legend.position as NormalizedPoint).x,
        y: (legend.position as NormalizedPoint).y,
      },
      background: { ...defaults.legend.background, enabled: legend.background as boolean },
    },
    scaleBar: defaults.scaleBar,
    northArrow: defaults.northArrow,
    sourcesAttribution: defaults.sourcesAttribution,
    logo: null,
    insetMap: null,
  };
  return isCompositionDraftValid(migrated) ? migrated : null;
}
